import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FOLLOW_UP_KIND_LABELS, type FollowUp, type FollowUpKind, type Paginated } from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { useSession } from '../../session';
import './crm.css';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const dayFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
export const dayOf = (iso: string) => dayFormat.format(new Date(`${iso}T12:00:00`));
export const addDays = (days: number) => new Date(Date.now() + days * 86_400_000).toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
export const subjectPath = (kind: FollowUpKind, id: string) => (kind === 'inquiry' ? `/anfragen/${id}` : kind === 'offer' ? `/angebote/${id}` : kind === 'order' ? `/auftraege/${id}` : `/kontakte/${id}`);

/** Alle Abfragen der Wiedervorlagen neu laden, wenn sich eine geändert hat */
export function useFollowUpRefresh() {
  const client = useQueryClient();
  return () => {
    for (const key of ['follow-ups', 'crm-overview', 'order-pipeline']) client.invalidateQueries({ queryKey: [key] });
  };
}

/** Wiedervorlage erledigen oder verschieben: die zwei Schalter hinter jeder Zeile */
export function FollowUpActions({ item, onChanged }: { item: FollowUp; onChanged?: () => void }) {
  const refresh = useFollowUpRefresh();
  const [mode, setMode] = useState<'done' | 'move' | null>(null);
  const [text, setText] = useState('');
  const [date, setDate] = useState(addDays(3));
  const [error, setError] = useState<string | null>(null);
  const finish = () => {
    refresh();
    onChanged?.();
    setMode(null);
    setText('');
    setError(null);
  };
  const done = useMutation({ mutationFn: () => api('POST', `/crm/follow-ups/${item.id}/done`, { resultNote: text.trim() || undefined }), onSuccess: finish, onError: (e) => setError(errorText(e, 'Das hat nicht geklappt.')) });
  const move = useMutation({ mutationFn: () => api('PATCH', `/crm/follow-ups/${item.id}`, { dueOn: date }), onSuccess: finish, onError: (e) => setError(errorText(e, 'Das hat nicht geklappt.')) });
  const remove = useMutation({ mutationFn: () => api('DELETE', `/crm/follow-ups/${item.id}`), onSuccess: finish, onError: (e) => setError(errorText(e, 'Das hat nicht geklappt.')) });
  return (
    <div className="fu-actions">
      <div className="fu-actions__row">
        <button type="button" className="btn btn--primary btn--sm" onClick={() => setMode(mode === 'done' ? null : 'done')}>Erledigt</button>
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setMode(mode === 'move' ? null : 'move')}>Verschieben</button>
        <button type="button" className="btn btn--secondary btn--sm" aria-label="Wiedervorlage löschen" onClick={() => window.confirm('Diese Wiedervorlage löschen?') && remove.mutate()}>Löschen</button>
      </div>
      {mode === 'done' && (
        <form className="fu-form" onSubmit={(e: FormEvent) => { e.preventDefault(); done.mutate(); }}>
          <label className="field"><span>Ergebnis (freiwillig)</span><input className="input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Zum Beispiel: Meldet sich nächste Woche" /></label>
          <button type="submit" className="btn btn--primary btn--sm" disabled={done.isPending}>Als erledigt markieren</button>
        </form>
      )}
      {mode === 'move' && (
        <form className="fu-form" onSubmit={(e: FormEvent) => { e.preventDefault(); move.mutate(); }}>
          <label className="field"><span>Neues Datum</span><input className="input" type="date" value={date} min={addDays(0)} onChange={(e) => setDate(e.target.value)} required /></label>
          <button type="submit" className="btn btn--primary btn--sm" disabled={move.isPending}>Verschieben</button>
        </form>
      )}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
    </div>
  );
}

/** Eine Wiedervorlage als Zeile: fällig seit, Bezug, Kunde, Notiz */
export function FollowUpLine({ item, showSubject = true }: { item: FollowUp; showSubject?: boolean }) {
  const late = item.overdueDays > 0 && !item.doneAt;
  return (
    <div className="fu-line">
      <div className="fu-line__main">
        <span className={`fu-due${late ? ' is-late' : item.overdueDays === 0 && !item.doneAt ? ' is-today' : ''}`}>
          {item.doneAt ? `Erledigt ${dayOf(item.doneAt.slice(0, 10))}` : item.overdueDays > 0 ? `${item.overdueDays} ${item.overdueDays === 1 ? 'Tag' : 'Tage'} überfällig` : item.overdueDays === 0 ? 'Heute' : `am ${dayOf(item.dueOn)}`}
        </span>
        {showSubject && (
          <span>
            <span className="muted small">{FOLLOW_UP_KIND_LABELS[item.subjectKind]}: </span>
            {item.subjectLabel ? <Link to={subjectPath(item.subjectKind, item.subjectId)} className="strong-link">{item.subjectLabel}</Link> : <span className="muted">nicht sichtbar</span>}
            {item.contactName && <span className="muted"> · {item.contactName}</span>}
          </span>
        )}
      </div>
      {item.note && <div className="fu-note">{item.note}</div>}
      {item.resultNote && <div className="fu-note">Ergebnis: {item.resultNote}</div>}
      <div className="muted small">{item.assignedName ? `Zuständig: ${item.assignedName}` : 'Niemandem zugeteilt'}{item.dueOn && !item.doneAt && item.overdueDays <= 0 ? ` · fällig ${dayOf(item.dueOn)}` : ''}{item.doneByName ? ` · von ${item.doneByName}` : ''}</div>
    </div>
  );
}

/**
 * Wiedervorlagen an einem Beleg (Anfrage, Angebot, Auftrag, Kontakt): zeigt die offenen und erledigten und legt neue an. So fragt niemand zweimal, und nichts bleibt liegen.
 * Erscheint nur mit dem Modul CRM-Abteilung und dem Leserecht.
 */
export function FollowUpPanel({ subjectKind, subjectId }: { subjectKind: FollowUpKind; subjectId: string }) {
  const { can, hasModule } = useSession();
  const refresh = useFollowUpRefresh();
  const enabled = hasModule('crm') && can('crm.read');
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState(addDays(7));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['follow-ups', { subjectKind, subjectId }],
    queryFn: async () => (await api<Paginated<FollowUp>>('GET', `/crm/follow-ups${queryString({ subjectKind, subjectId, scope: 'open', perPage: 50 })}`)).data,
    enabled,
  });
  const history = useQuery({
    queryKey: ['follow-ups', { subjectKind, subjectId, done: true }],
    queryFn: async () => (await api<Paginated<FollowUp>>('GET', `/crm/follow-ups${queryString({ subjectKind, subjectId, scope: 'done', perPage: 20 })}`)).data,
    enabled: enabled && open,
  });
  const create = useMutation({
    mutationFn: () => api('POST', '/crm/follow-ups', { subjectKind, subjectId, dueOn: date, note: note.trim() || undefined }),
    onSuccess: () => {
      refresh();
      setAdding(false);
      setNote('');
      setError(null);
    },
    onError: (e) => setError(errorText(e, 'Die Wiedervorlage konnte nicht angelegt werden.')),
  });
  if (!enabled) return null;
  const openItems = list.data ?? [];
  const due = openItems.filter((f) => f.overdueDays >= 0).length;

  return (
    <CollapsibleCard title={`Wiedervorlagen${openItems.length ? ` (${openItems.length})` : ''}`} storageKey="follow-ups" open={open || due > 0 ? true : undefined} onOpenChange={setOpen} defaultOpen={false}
      actions={can('crm.write') ? <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setAdding(!adding); setOpen(true); }}>Wiedervorlage anlegen</button> : undefined}>
      <div className="fu-panel">
        {adding && (
          <form className="fu-form fu-form--wide" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
            <label className="field"><span>Nachfassen am</span><input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></label>
            <label className="field fu-form__grow"><span>Notiz (freiwillig)</span><input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="Zum Beispiel: Nachfragen, ob das Angebot passt" /></label>
            <div className="fu-actions__row">
              <button type="submit" className="btn btn--primary btn--sm" disabled={create.isPending}>Anlegen</button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAdding(false)}>Abbrechen</button>
            </div>
          </form>
        )}
        {error && <div className="alert alert--error" role="alert">{error}</div>}
        {list.isLoading && <p className="muted small">Wird geladen …</p>}
        {openItems.length === 0 && !list.isLoading && <p className="muted small">Keine offene Wiedervorlage.</p>}
        {openItems.map((f) => (
          <div key={f.id} className="fu-item">
            <FollowUpLine item={f} showSubject={false} />
            {can('crm.write') && <FollowUpActions item={f} />}
          </div>
        ))}
        {(history.data?.length ?? 0) > 0 && (
          <>
            <h3 className="fu-sub">Erledigt</h3>
            {history.data!.map((f) => <div key={f.id} className="fu-item"><FollowUpLine item={f} showSubject={false} /></div>)}
          </>
        )}
      </div>
    </CollapsibleCard>
  );
}
